import { useEffect, useState } from 'react';
import Field from '../../components/common/Field';
import Spinner from '../../components/common/Spinner';

const CalIcon = () => (
  <svg className="cal-ic" viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>
);

/* A reminder's link is typed by hand: a bare domain gets https://, and
   anything with some other scheme (javascript:, mailto:) is refused rather
   than saved. '' for blank, null for a refused value. */
export function normalizeReminderLink(raw) {
  const u = String(raw || '').trim();
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  if (/^[a-z][a-z0-9+.-]*:/i.test(u)) return null;
  return `https://${u}`;
}

/* Add/edit a reminder: the text, plus an optional date and time, an optional
   link and an optional deadline. `editing` is a normalized reminder to
   update, or null to add a new one. */
export default function ReminderForm({ open, editing, saving, onClose, onSave }) {
  const [text, setText] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [link, setLink] = useState('');
  const [deadline, setDeadline] = useState('');

  useEffect(() => {
    setText(editing?.text || '');
    setDate(editing?.date || '');
    setTime(editing?.time || '');
    setLink(editing?.link || '');
    setDeadline(editing?.deadline || '');
  }, [editing, open]);

  const save = () => {
    if (!text.trim()) {
      window.alert('Write the reminder first.');
      return;
    }
    const url = normalizeReminderLink(link);
    if (url === null) {
      window.alert('Enter a web address for the link, like https://roomparent.com.');
      return;
    }
    onSave({ text: text.trim(), date, time, link: url, deadline });
  };

  return (
    <div className={`inline-form ${open ? 'on' : ''}`}>
      <div className="form-grid">
        <Field label="Reminder" style={{ gridColumn: '1/-1' }}>
          <input
            type="text"
            value={text}
            maxLength={500}
            placeholder="e.g. Picture Day — wear the class shirt!"
            onChange={(e) => setText(e.target.value)}
          />
        </Field>
        <Field label="Date (optional)">
          <div className="date-input-wrap">
            <CalIcon />
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
        </Field>
        {/* bare native time input — its own clock sits at the end of the field,
            unlike the date wrapper whose icon is drawn on the left. It submits
            "HH:mm", which is exactly what the API stores. */}
        <Field label="Time (optional)">
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
        </Field>
        {/* somewhere the reminder points — a form, a sign-up, a school page */}
        <Field label="Link (optional)">
          <input
            type="text"
            inputMode="url"
            autoComplete="off"
            value={link}
            placeholder="https://…"
            onChange={(e) => setLink(e.target.value)}
          />
        </Field>
        {/* by when parents need to act — not the day the reminder is about */}
        <Field label="Deadline (optional)" hint="By when parents need to act.">
          <div className="date-input-wrap">
            <CalIcon />
            <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
          </div>
        </Field>
      </div>
      <div className="inline-form-actions">
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" disabled={saving} onClick={save}>
          {saving ? <><Spinner /> Saving…</> : editing ? 'Save Changes' : 'Save Reminder'}
        </button>
      </div>
    </div>
  );
}
